import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CHAT_BOT } from '__queries/query-keys';
import MachineLearningService from 'services/MachineLearningService';
import { toastVariant, useToast } from '__components/ToastProvider';
import {
  actionType,
  useChatProgress,
} from '__pages/BusinessBuilder/useChatProgress';
import { deferExecution } from 'utility/defer';
import { ChatBot } from 'ts-components/ChatBot';
import { OutputVariableRequirements } from 'ts-components/ChatBot/types';
import { FlowStatusFromAPI } from 'ts-components/ChatBot/types';
import Loader from '__components/Kizen/Loader';
import { Indicator } from './Indicator';

export const FLOW_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

export const FLOW_TYPE = {
  EMAIL_BUILDER: 'email_builder',
  BUSINESS_BUILDER: 'business_builder',
  CUSTOM_OBJECT_EDITOR: 'custom_object_editor',
  FORM_BUILDER: 'form_builder',
};

export const TASK_TYPE = {
  FORM_PLAN_GENERATION: 'form_plan_generation',
  EMAIL_PLAN_GENERATION: 'email_plan_generation',
  CUSTOM_OBJECT_EDITOR_PLANNER: 'custom_object_editor_planner',
  BUSINESS_STATEGIC_PLAN_GENERATION: 'business_strategic_plan_generation',
};

type HeaderRenderArgs = {
  flowStatus: any;
  currentStep: number;
  isGenerationStarted: boolean;
  isStartGenerationLoading: boolean;
};

type ConversationProps = {
  enabled?: boolean;
  isMobile?: boolean;
  inModal?: boolean;
  context?: Record<string, string>;
  objectId?: string;
  flowType?: string;
  taskType?: string;
  header?: (args: HeaderRenderArgs) => JSX.Element | null;
  onComplete?: (id: string) => void;
  onError?: (error: any) => void;
  onSessionChange?: (chatSession: string) => void;
  topPadding?: number;
  entityName?: string;
  pendingMessages?: Array<string>;
};

export const Conversation = ({
  enabled = true,
  isMobile = false,
  inModal = false,
  context,
  objectId,
  flowType = FLOW_TYPE.BUSINESS_BUILDER,
  taskType = TASK_TYPE.BUSINESS_STATEGIC_PLAN_GENERATION,
  header = () => null,
  onComplete,
  onError,
  onSessionChange,
  topPadding,
  entityName,
  pendingMessages = [],
}: ConversationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const modalState = useRef({ ready: false, intialLoad: false });
  const flowStatusCallsRef = useRef(0);
  const [showToast] = useToast();

  const { state, dispatch } = useChatProgress();
  const {
    initialLoading,
    isGenerationStarted,
    isGenerationComplete,
    isResetting,
  } = state;

  useEffect(() => {
    if (inModal && !modalState.current.intialLoad) {
      dispatch({ type: actionType.RESET_CHAT });
    }
  }, [inModal, dispatch]);

  const fetchChatSession = useCallback(async () => {
    dispatch({ type: actionType.START_FETCHING_CHAT_SESSION });
    try {
      const results = await MachineLearningService.getNewChatSession(
        taskType,
        context
      );
      deferExecution(() => {
        dispatch({ type: actionType.CHAT_SESSION_FETCHED });
      });
      return results;
    } catch (e: any) {
      let errorMessage = t('Error fetching chat session');
      const nonFieldErrors = e?.response?.data?.context?.non_field_errors;
      if (nonFieldErrors) {
        errorMessage = nonFieldErrors[0];
      }
      onError?.(e);
      showToast({
        variant: toastVariant.FAILURE,
        message: errorMessage,
      });
      throw e;
    }
  }, [dispatch, context, taskType, t, showToast, onError]);

  const {
    data: newChatSession,
    isLoading: isNewChatSessionLoading,
    isError: isNewChatSessionError,
  } = useQuery({
    queryKey: CHAT_BOT.NEW_CHAT_SESSION(taskType, context),
    queryFn: fetchChatSession,
    retry: false,
    enabled: enabled,
    throwOnError: false,
  });

  const { isLoading: isFlowStatusLoading, data: flowStatus } = useQuery({
    queryKey: CHAT_BOT.FLOW_STATUS(flowType, objectId, newChatSession?.id),
    queryFn: async () => {
      try {
        let flowStatus;
        flowStatus = await MachineLearningService.getFlowStatus(
          flowType,
          objectId
        );
        if (inModal) {
          if (
            flowStatus.status !== FLOW_STATUS.IN_PROGRESS &&
            flowStatus.status !== FLOW_STATUS.FAILED &&
            !(
              flowStatus.status === FLOW_STATUS.COMPLETED && isGenerationStarted
            )
          ) {
            flowStatus = await MachineLearningService.getFlowStatus(
              flowType,
              objectId,
              newChatSession?.id
            );
          }
        }
        flowStatusCallsRef.current++;
        modalState.current = { ...modalState.current, intialLoad: true };
        if (
          (flowStatus?.status === FLOW_STATUS.COMPLETED ||
            flowStatus?.status === FLOW_STATUS.FAILED) &&
          isGenerationStarted &&
          !isGenerationComplete &&
          !isResetting
        ) {
          dispatch({ type: actionType.GENERATION_COMPLETE });
        }
        return flowStatus;
      } catch (error: any) {
        modalState.current = { ...modalState.current, intialLoad: true };
        if (error?.response?.status === 400) {
          const errorMessage = t('Error fetching generation status');
          onError?.(error);
          showToast({
            variant: toastVariant.FAILURE,
            message: errorMessage,
          });
        }
        if (error?.response?.status === 404) {
          return {
            status: 'not_started',
          };
        }
        throw error;
      }
    },
    retry: (error: any) => error?.response?.status !== 404,
    refetchInterval: isGenerationStarted ? 2000 : false, // once we complete a flow check every 2 seconds for completion
    enabled: !!newChatSession?.id && enabled,
  });

  const { data: modelConfigMetadata } = useQuery({
    queryKey: CHAT_BOT.METADATA,
    queryFn: async () => {
      return MachineLearningService.getChatMetadata();
    },
    enabled: true,
  });

  const streamEventListeners = useMemo(() => {
    const eventListenerNames =
      modelConfigMetadata?.[taskType]?.output_variables?.reduce(
        (acc: string[], { name, is_required }: OutputVariableRequirements) => {
          if (is_required && !name.includes('json')) {
            acc.push(name);
          }
          return acc;
        },
        []
      ) ?? [];
    return eventListenerNames;
  }, [modelConfigMetadata, taskType]);

  const chatSessionIdRef = useRef(newChatSession?.id);
  const handleDeleteSession = useCallback(async () => {
    if (chatSessionIdRef.current !== newChatSession?.id) {
      await MachineLearningService.deleteChatSession(newChatSession?.id);
      dispatch({ type: actionType.RESET_CHAT });
      queryClient.invalidateQueries({
        queryKey: CHAT_BOT.NEW_CHAT_SESSION(taskType, context),
      });
    }
  }, [dispatch, queryClient, newChatSession, taskType, context]);

  const newChatSessionId = newChatSession?.id;

  // onSessionChange callback
  useEffect(() => {
    if (newChatSessionId) {
      onSessionChange?.(newChatSessionId);
    }
  }, [newChatSessionId, onSessionChange]);

  const startGeneration = useCallback(
    async (completedSessionId: string) => {
      queryClient.setQueryData(
        CHAT_BOT.FLOW_STATUS(flowType),
        (prev: FlowStatusFromAPI) => {
          return {
            ...prev,
            status: FLOW_STATUS.IN_PROGRESS,
            run_method: {},
          };
        }
      );
      try {
        await MachineLearningService.completeChatSession(completedSessionId);
        await MachineLearningService.startFlow({
          type: flowType,
          session_id: completedSessionId,
        });
        deferExecution(() => {
          queryClient.invalidateQueries({
            queryKey: CHAT_BOT.FLOW_STATUS(flowType),
          });
          dispatch({ type: actionType.START_GENERATION });
        });
      } catch (error: any) {
        const errorMessage = error.response?.data?.already_running
          ? t('A flow is already in progress, please try again later')
          : t('Error starting generation');
        showToast({
          variant: toastVariant.FAILURE,
          message: errorMessage,
        });
        onError?.(error);
      }
    },
    [dispatch, queryClient, flowType, t, showToast, onError]
  );

  const startGenerationMutation = useMutation({
    mutationFn: startGeneration,
  });

  const currentStep = useMemo(() => {
    // add dashboard builder step once ready
    if (isGenerationStarted) {
      return 2;
    } else {
      return 1;
    }
  }, [isGenerationStarted]);

  // if user tries using chat while generation flow is in progress
  useEffect(() => {
    if (
      flowStatus?.status === FLOW_STATUS.IN_PROGRESS ||
      (flowStatus?.status === FLOW_STATUS.COMPLETED && !inModal)
    ) {
      dispatch({ type: actionType.START_GENERATION });
    }
  }, [flowStatus, inModal, dispatch]);

  const isStartGenerationLoading = startGenerationMutation.isPending;

  // Handles resetting the state specifically when it's used inside a modal,
  // because there are some special conditions we need to account for, like firing a callback after
  // the session completes to close the modal
  useEffect(() => {
    if (inModal && isGenerationComplete) {
      if (!modalState.current.ready) {
        dispatch({ type: actionType.RESET_CHAT });
        modalState.current = { ready: true, intialLoad: false };
      } else if (flowStatusCallsRef.current > 1) {
        modalState.current = { ready: false, intialLoad: false };
        dispatch({ type: actionType.RESET_CHAT });
        onComplete?.(flowStatus);
        flowStatusCallsRef.current = 0;
      } else {
        flowStatusCallsRef.current = 0;
        handleDeleteSession();
      }
    }
    if (isGenerationStarted && inModal) {
      modalState.current = { ready: true, intialLoad: false };
    }
  }, [
    dispatch,
    t,
    showToast,
    isGenerationComplete,
    handleDeleteSession,
    inModal,
    onComplete,
    flowStatus,
    isGenerationStarted,
  ]);

  const showChat = useMemo(() => {
    const flowStatusCondition = inModal
      ? flowStatus?.status === FLOW_STATUS.NOT_STARTED ||
        flowStatus?.status === FLOW_STATUS.COMPLETED
      : flowStatus?.status === FLOW_STATUS.NOT_STARTED;

    const baseConditions =
      !isGenerationComplete &&
      !isGenerationStarted &&
      !isFlowStatusLoading &&
      !initialLoading &&
      flowStatusCondition &&
      enabled;

    return inModal
      ? modalState.current.intialLoad && baseConditions
      : baseConditions;
  }, [
    inModal,
    flowStatus,
    isGenerationComplete,
    isGenerationStarted,
    isFlowStatusLoading,
    initialLoading,
    enabled,
  ]);

  const showGenerationLoader = useMemo(
    () => inModal && isGenerationStarted && !isGenerationComplete,
    [isGenerationStarted, isGenerationComplete, inModal]
  );

  const showLoader = useMemo(
    () => inModal && !showChat && !showGenerationLoader,
    [showChat, showGenerationLoader, inModal]
  );

  return (
    <>
      {header({
        currentStep,
        isGenerationStarted,
        flowStatus,
        isStartGenerationLoading,
      })}
      {showChat ? (
        <ChatBot
          entityName={entityName}
          isMobile={isMobile}
          inModal={inModal}
          errorMessage={
            isNewChatSessionError ? 'Error fetching chat session' : ''
          }
          chatSessionId={newChatSession?.id}
          fetchSession={MachineLearningService.getChatHistory}
          onCompleteSession={startGenerationMutation.mutate}
          getStreamingEndpoint={MachineLearningService.getStreamingEndpoint}
          eventListenerNames={streamEventListeners}
          onDeleteSession={handleDeleteSession}
          newChatSessionLoading={isNewChatSessionLoading}
          completeSessionLoading={
            flowStatus?.status === FLOW_STATUS.IN_PROGRESS
          }
          topPadding={
            typeof topPadding === 'number' ? String(topPadding) : topPadding
          }
        />
      ) : null}
      {/* When in a modal we need custom loading UI */}
      {showLoader ? (
        <div className="pb-48">
          <Loader loading />
        </div>
      ) : null}
      {showGenerationLoader ? <Indicator messages={pendingMessages} /> : null}
    </>
  );
};
